import { useEffect, useRef } from "react"
import { CommonActions } from "@react-navigation/native"
import { useFeatureFlag } from "../hooks/useFeatureFlag"
import { usePostHog } from "../hooks/usePosthog"
import { navigationRef } from "../services/NavigationService"
import { Sentry } from "../utils/Sentry"
import {
  consumePlaidOAuthReturnUri,
  getPlaidOAuthReturnUri,
  loadPlaidOAuthResume,
  PLAID_OAUTH_RESUME_FLAG,
} from "../utils/plaidOAuthResume"

const MISSING_STATE_ROUTE = "PlaidOAuthReturnMissing"

/**
 * Plaid sends the customer back to the web app root with `oauth_state_id`,
 * often in a new tab. Put them back into the flow that opened Link; the flow's
 * PlaidProvider then re-opens Link with the redirect URI.
 */
export function PlaidOAuthReturnHandler({ isNavigationReady }: { isNavigationReady: boolean }) {
  const isEnabled = useFeatureFlag(PLAID_OAUTH_RESUME_FLAG)
  const posthog = usePostHog()
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current || !isNavigationReady || !getPlaidOAuthReturnUri()) {
      return
    }
    const record = loadPlaidOAuthResume()
    if (!record && isEnabled === undefined) {
      return
    }
    handled.current = true
    if (!record && !isEnabled) {
      return
    }

    const target = record ? record.flow : MISSING_STATE_ROUTE
    if (!navigationRef.getRootState()?.routeNames.includes(target)) {
      return
    }
    // The linking config mirrors window.location, so drop the spent state id from the URL.
    window.history.replaceState(window.history.state, "", "/")

    if (record) {
      Sentry.addBreadcrumb({
        category: "plaid",
        message: "Plaid OAuth return: resuming flow",
        level: "info",
        data: { flow: record.flow, ageMs: Date.now() - record.savedAt },
      })
      navigationRef.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: record.flow, params: record.flowParams }],
        })
      )
      return
    }

    posthog.capture("plaid_oauth_resume_missing_state")
    Sentry.captureMessage("Plaid OAuth return without a saved flow", { level: "warning" })
    consumePlaidOAuthReturnUri()
    navigationRef.dispatch(
      CommonActions.reset({ index: 0, routes: [{ name: MISSING_STATE_ROUTE }] })
    )
  }, [isEnabled, isNavigationReady, posthog])

  return null
}
